import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { AuthContext } from '../contexts/AuthContext';
import api from '../utils/api';
import { SplitAuthLayout, AmberButton } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, hasActiveSubscription } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (authLoading) return;
      if (!isAuthenticated) {
        localStorage.setItem('redirectAfterAuth', '/checkout');
        navigate('/login', { state: { message: 'Please log in to continue with payment', from: '/checkout' } });
        return;
      }
      if (hasActiveSubscription) {
        navigate('/dashboard', { state: { message: 'You already have an active subscription.' } });
        return;
      }
      try {
        setLoading(true);
        let productData = null;
        if (location.state?.priceId) {
          productData = location.state;
        } else {
          const response = await api.get(`/api/payments/products/${process.env.REACT_APP_STRIPE_PRODUCT_ID}`);
          productData = {
            id: response.data.id,
            name: response.data.name,
            price: response.data.defaultPrice?.unitAmount || 0,
            priceId: response.data.defaultPrice?.id,
            description: response.data.description || '',
            recurringInterval: response.data.defaultPrice?.recurringInterval || 'year',
          };
        }
        setProduct(productData);
      } catch (err) {
        setError('Failed to load payment information. Please try again.');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [isAuthenticated, authLoading, navigate, hasActiveSubscription, location.state]);

  const handleCheckout = async () => {
    if (!product?.priceId) { setError('Invalid product information'); return; }
    try {
      setLoading(true);
      const response = await api.post('/api/payments/create-checkout-session', { priceId: product.priceId });
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to create checkout session. Please try again.');
      console.error('Checkout error:', err);
      setLoading(false);
    }
  };

  const formatPrice = (amount) => {
    if (!amount) return '$500';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  if (authLoading) {
    return (
      <SplitAuthLayout statusLine="// one step to go · 256-bit encrypted">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <CircularProgress size={20} sx={{ color: '#2dd4bf' }} />
          <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd' }}>Loading…</Box>
        </Box>
      </SplitAuthLayout>
    );
  }

  if (error) {
    return (
      <SplitAuthLayout statusLine="// checkout error">
        <Box component="h1" sx={{ m: 0, mb: '16px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
          Something went wrong
        </Box>
        <Alert severity="error" sx={{ mb: '24px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <AmberButton onClick={() => window.location.reload()}>Try again</AmberButton>
          <Box
            component="button"
            onClick={() => navigate('/')}
            sx={{ background: 'transparent', border: '1px solid rgba(120,150,200,.28)', color: '#a9bcdc', fontWeight: 500, fontSize: '14px', px: '16px', py: '10px', borderRadius: '9px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' } }}
          >
            Back
          </Box>
        </Box>
      </SplitAuthLayout>
    );
  }

  if (!product) return null;

  return (
    <SplitAuthLayout statusLine="// one step to go · 256-bit encrypted · Stripe">
      <Box component="h1" sx={{ m: 0, mb: '20px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
        Complete your subscription
      </Box>

      {/* Plan summary card */}
      <Box sx={{ border: '1px solid rgba(120,150,200,.24)', borderRadius: '13px', background: 'linear-gradient(180deg,#0e1c39,#0a1326)', overflow: 'hidden', mb: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.18)', fontFamily: MONO, fontSize: '11px', letterSpacing: '.08em', color: '#90a6cc' }}>
          <Box>ANNUAL SUBSCRIPTION</Box>
          <Box sx={{ color: '#06121f', background: '#2dd4bf', borderRadius: '999px', px: '9px', py: '3px', fontSize: '10px' }}>ALL FEATURES</Box>
        </Box>
        <Box sx={{ px: '16px', py: '18px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#8298bd' }}>one user · unlimited drawings</Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <Box sx={{ fontFamily: MONO, fontSize: '30px', fontWeight: 600, color: '#f4f8ff' }}>
              {formatPrice(product.price)}
            </Box>
            <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#8298bd', whiteSpace: 'nowrap' }}>
              / {product.recurringInterval || 'yr'}
            </Box>
          </Box>
        </Box>
      </Box>

      <AmberButton
        size="lg"
        fullWidth
        disabled={loading}
        onClick={handleCheckout}
        sx={{ mb: '16px' }}
      >
        {loading
          ? <CircularProgress size={18} sx={{ color: '#241200' }} />
          : <><span>Proceed to payment</span> <Box component="span" sx={{ fontFamily: MONO }}>→</Box></>
        }
      </AmberButton>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: MONO, fontSize: '11.5px', color: '#5f7398' }}>
        <LockIcon sx={{ fontSize: '13px' }} />
        secure payment via Stripe
      </Box>
    </SplitAuthLayout>
  );
};

export default Checkout;
